import React, { useEffect, useState, useMemo } from 'react'
import { NavLink } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useSearchParams, useLocation, createSearchParams } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { MenuItem } from '@mui/material'
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined'
import ChecklistRtlOutlinedIcon from '@mui/icons-material/ChecklistRtlOutlined'
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined'
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined'
import ManageAccountsOutlinedIcon from '@mui/icons-material/ManageAccountsOutlined'
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined'
import { toast } from 'react-toastify'
import { useNavigate } from 'react-router-dom'

import { requestStatuses } from '../../core/constants'
import { identifyUser } from '../../core'
import { fetchRequestsCount } from '../../store/slices/requests/slice'
import { fetchMe } from '../../store/slices/users/slice'
import {
  fetchCompanies,
  fetchSelectedCompany,
  setSelectedCompany,
} from '../../store/slices/companies/slice'

import { Bagde, BagdeSmall, Select, Text } from '..'
import {
  DashboardIcon,
  FleetIcon,
  RentsIcon,
  RequestIcon,
  TransactionsIcon,
  LogoIcon,
  UsersIcon,
  ChevronDownIcon,
} from '../icons'
import {
  Aside,
  AsideContent,
  AsideHeader,
  Chevron,
  CompanySelectBox,
  CompanyWrapper,
  ItemAttention,
  AsideUser,
  ParentRow,
  MainUl,
  SubUl,
} from './styled'
import SidebarBottomBlock from './sidebar-bottom-block'
import { BusinessType, OffersCard } from './business-info'
import { fetchCouriersCount } from '../../store/slices/couriers/slice'
import { fetchRentsCount } from '../../store/slices/rents/slice'
import { fetchCapabilities } from '../../store/slices/capabilities/slice'
import { buildAccess } from '../../core/helpers/access'
import { useMobileShell } from '../layout/mobile-shell'

const Sidebar = ({ open = false, onNavigate }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const [searchParams] = useSearchParams()

  const { requestsCount } = useSelector(state => state.requests)
  const { currentUser } = useSelector(state => state.users)
  const { companies, needsOnboarding } = useSelector(state => state.companies)
  const selectedCompany = useSelector(state => state.companies.selected_company)
  const { couriersCount } = useSelector(state => state.couriers)
  const { rentsCount } = useSelector(state => state.rents)
  const capabilities = useSelector(state => state.capabilities)
  const { modules: allowedModules } = capabilities
  const { setNavItems } = useMobileShell()

  const [openItems, setOpenItems] = useState({})

  // Именно ?? : "все компании" приходят с id=0, а ноль falsy - через || выбор
  // схлопывался в пустую строку и в селекте не было видно, что выбрано
  const selectedCompanyId = selectedCompany?.id ?? ''

  const listSeenKey = selectedCompanyId ? `couriers_list_seen_count_${selectedCompanyId}` : null
  const lastListSeenCount = useMemo(() => {
    if (!listSeenKey) return 0
    const raw = localStorage.getItem(listSeenKey)
    const n = Number(raw)
    return Number.isFinite(n) ? n : 0
  }, [listSeenKey, pathname, couriersCount, selectedCompanyId])

  const hasNewCourier = !!selectedCompanyId && (couriersCount || 0) > lastListSeenCount

  const rentsListSeenKey = selectedCompanyId ? `rents_list_seen_count_${selectedCompanyId}` : null
  const lastRentsListSeenCount = useMemo(() => {
    if (!rentsListSeenKey) return 0
    const raw = localStorage.getItem(rentsListSeenKey)
    const n = Number(raw)
    return Number.isFinite(n) ? n : 0
  }, [rentsListSeenKey, pathname, rentsCount, selectedCompanyId])

  const hasNewRent = !!selectedCompanyId && (rentsCount || 0) > lastRentsListSeenCount

  useEffect(() => {
    if (needsOnboarding) navigate('/register')
  }, [needsOnboarding, navigate])

  useEffect(() => {
    dispatch(fetchCouriersCount())
  }, [dispatch])

  useEffect(() => {
    dispatch(fetchRentsCount())
  }, [dispatch])

  const access = buildAccess(currentUser, capabilities)
  const isManager = currentUser?.user_type === 'manager' || currentUser?.is_superuser

  // Вложенный список - только там, где у раздела есть разные срезы одного
  // объекта: курьер и приглашение курьера, аренда и её тариф. Парк живёт
  // одной страницей с переключателем «карта/список», и дробить его в меню
  // значило бы дублировать этот переключатель.
  //
  // Задачи вынесены из сервиса намеренно: перевезти байк между точками,
  // съездить за запчастями, провести инвентаризацию - это не ремонт, и
  // искать их внутри «Сервиса» никто не станет.
  // Наши иконки нарисованы заливкой #2B2B2B с прозрачностью 0.54 и цвет не
  // наследуют. Иконки из набора MUI с color: inherit синели на активном
  // пункте, и половина меню выглядела иначе, чем вторая. Приводим их к тем
  // же цвету и прозрачности, пока старые не переведены на наследование
  const iconSx = { fontSize: 20, color: '#2B2B2B', opacity: 0.54 }
  const sidebarItemsAll = [
    {
      title: t('dashboard.sidebarTitle'),
      module: 'analytics',
      permission: 'dashboard_metrics',
      icon: <DashboardIcon />,
      childrens: [],
      link: { pathname: '/dashboard', search: '' },
    },
    {
      title: t('fleet.sidebarTitle'),
      module: 'monitoring',
      permission: 'bike_list',
      icon: <FleetIcon />,
      childrens: [],
      link: { pathname: '/fleet/map', search: '' },
    },
    {
      title: t('drivers.sidebarTitle'),
      module: 'couriers',
      permission: 'courier_list',
      icon: <UsersIcon />,
      childrens: [
        { title: 'Список', link: { pathname: '/drivers/list', search: '' } },
        {
          title: t('invitations.sidebarTitle'),
          link: {
            pathname: access.can('courier_invite') ? '/invitations/couriers' : '/invitations/list',
            search: '',
          },
        },
      ],
      badgeDot: hasNewCourier,
      badgeTitle: 'Появился новый курьер — откройте список, чтобы посмотреть',
    },
    {
      title: t('rents.sidebarTitle'),
      module: 'rents',
      permission: 'rent_list',
      icon: <RentsIcon />,
      childrens: [
        { title: 'Список', link: { pathname: '/rents/list', search: '' } },
        { title: t('rates.sidebarTitle', 'Тарифы'), link: { pathname: '/rates/list', search: '' } },
      ],
      badgeDot: hasNewRent,
      badgeTitle: 'Появилась новая аренда — откройте список, чтобы посмотреть',
    },
    {
      title: 'Задачи',
      module: 'repairs',
      permission: 'task_list',
      icon: <ChecklistRtlOutlinedIcon sx={iconSx} />,
      childrens: [],
      link: { pathname: '/tasks', search: '' },
    },
    // Заявка, наряд и прайс - три разные вещи, а не срезы одного объекта:
    // заявку приносит клиент, наряд ведёт механик, прайс правит владелец, и
    // открывают их разные люди в разное время. Собранные под «Сервисом» они
    // прятались за лишним щелчком, и догадаться, что прайс лежит именно там,
    // было нельзя
    {
      title: t('requests.sidebarTitle'),
      module: 'repairs',
      permission: 'service_request_list',
      icon: <BuildOutlinedIcon sx={iconSx} />,
      childrens: [],
      link: { pathname: '/requests', search: '' },
    },
    {
      title: 'Заказ-наряды',
      module: 'repairs',
      permission: 'work_order_list',
      icon: <RequestIcon />,
      childrens: [],
      link: { pathname: '/work-orders', search: '' },
    },
    {
      title: 'Прайс услуг',
      module: 'repairs',
      permission: 'service_item_list',
      icon: <LocalOfferOutlinedIcon sx={iconSx} />,
      childrens: [],
      link: { pathname: '/service-price', search: '' },
    },
    {
      title: 'Склад',
      module: 'warehouse',
      permission: 'warehouse_stock_list',
      icon: <Inventory2OutlinedIcon sx={iconSx} />,
      childrens: [],
      link: { pathname: '/warehouse', search: '' },
    },
    // Раздел назван по тому, что в нём лежит. «Деньги» с единственным
    // пунктом «Транзакции» внутри - лишний щелчок и вопрос «а что ещё там»
    {
      title: t('payments.sidebarTitle'),
      module: 'payments',
      permission: 'payment_list',
      icon: <TransactionsIcon />,
      childrens: [],
      link: { pathname: '/payments/list', search: '' },
    },
    // Только у того, кто управляет сотрудниками (владелец). Проверка строгая:
    // пока права не приехали, раздел не показываем
    {
      title: 'Сотрудники',
      strictPermission: 'staff_manage',
      icon: <ManageAccountsOutlinedIcon sx={iconSx} />,
      childrens: [],
      link: { pathname: '/staff', search: '' },
    },
    // Витрина пока пустая, поэтому на проде её не показываем. Но раздел
    // существует, и забывать про него не надо - локально он в меню есть.
    // localOnly гасит пункт в production-сборке, так что выкатить его
    // случайно нельзя
    {
      title: 'Витрина',
      localOnly: true,
      icon: <StorefrontOutlinedIcon sx={iconSx} />,
      childrens: [],
      link: { pathname: '/marketplace', search: '' },
    },
  ]

  // Раздел виден, если он входит в тариф компании. Источник правды - ответ
  // сервера, а не догадки фронта: тариф меняется в админке, релиз ради этого
  // выкатывать не надо.
  //
  // allowedModules === null означает "права ещё не приехали или не приехали
  // вовсе" - тогда показываем всё. Спрятать лишний пункт не страшно, он и так
  // закрыт на бэкенде, а пустое меню из-за сетевой икоты выглядит как
  // сломанная система. Суперадмин смотрит платформу целиком, его не режем.
  const isModuleAllowed = (item) =>
    !item.module || allowedModules === null || currentUser?.is_superuser ||
    allowedModules.includes(item.module)

  // Раздел нужен человеку, только если у его роли есть право на него. Модуль
  // тарифа и право роли - разные причины, поэтому проверяем оба. Права
  // приходят из capabilities: user_type для этого не годится, у механика он
  // тоже operator
  const isPermitted = (item) =>
    item.strictPermission
      ? access.can(item.strictPermission)
      : access.canSee(item.permission)

  // Разделы «только для разработки»: в сборке на прод их нет вообще.
  // NODE_ENV у create-react-app - development на yarn start и production на
  // yarn build, подменить его из окружения нельзя
  const isLocalOnlyAllowed = (item) =>
    !item.localOnly || process.env.NODE_ENV === 'development'

  const sidebarItems = sidebarItemsAll
    .map(item => (
      // Приглашения видны только тем, кто может приглашать
      item.childrens?.length && !access.can('invitation_list')
        ? { ...item, childrens: item.childrens.filter(
            child => child.title !== t('invitations.sidebarTitle')
          ) }
        : item
    ))
    .filter(isLocalOnlyAllowed)
    .filter(isModuleAllowed)
    .filter(isPermitted)

  // Нижние вкладки и название в верхней панели на телефоне строятся по тем
  // же пунктам, что и это меню: права считаем один раз, здесь
  const navSignature = sidebarItems
    .map(item => `${item.title}|${item.link?.pathname || item.childrens?.[0]?.link?.pathname}|${item.badgeDot ? 1 : 0}`)
    .join(';')
  useEffect(() => {
    setNavItems(sidebarItems)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navSignature])

  useEffect(() => {
    dispatch(fetchMe())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Права перечитываем при смене компании: у разных компаний владельца может
  // быть разный набор разделов
  useEffect(() => {
    dispatch(fetchCapabilities())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompany?.id])

  useEffect(() => {
    if (currentUser) identifyUser(currentUser, selectedCompany?.name)
  }, [currentUser, selectedCompany])

  useEffect(() => {
    dispatch(fetchRequestsCount())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname])

  useEffect(() => {
    dispatch(fetchCompanies())
  }, [dispatch])

  useEffect(() => {
    dispatch(fetchSelectedCompany())
  }, [dispatch])

  useEffect(() => {
    if (!selectedCompanyId || pathname !== '/drivers/list') return
    if (!couriersCount) return

    const key = `couriers_list_seen_count_${selectedCompanyId}`
    const current = Number(localStorage.getItem(key) || 0)
    if (couriersCount > current) localStorage.setItem(key, String(couriersCount))
  }, [pathname, couriersCount, selectedCompanyId])

  useEffect(() => {
    if (!selectedCompanyId || pathname !== '/rents/list') return
    if (!rentsCount) return

    const key = `rents_list_seen_count_${selectedCompanyId}`
    const current = Number(localStorage.getItem(key) || 0)
    if (rentsCount > current) localStorage.setItem(key, String(rentsCount))
  }, [pathname, rentsCount, selectedCompanyId])

  const handleCompanyChange = event => {
    const newCompanyId = event.target.value
    dispatch(setSelectedCompany({ id: newCompanyId }))
      .unwrap()
      .then(() => {
        window.location.reload()
      })
  }

  const getSectionRoot = p => '/' + (p?.split('/')?.[1] || '')
  const isSectionActive = (currentPath, itemPath) => getSectionRoot(currentPath) === getSectionRoot(itemPath)

  const toggleItem = key => {
    // Открыт всегда один раздел: раскрытые списки чужих разделов занимают
    // высоту и отвлекают, а вернуться в них - один щелчок
    setOpenItems(prev => (prev[key] ? {} : { [key]: true }))
  }

  // Ушли в другой раздел - свернули всё, что не относится к текущему месту.
  // Иначе, открыв «Транзакции», человек продолжал видеть раскрытыми
  // подпункты парка и курьеров
  useEffect(() => {
    const current = sidebarItems.find(
      item => item.childrens?.some(child => isSectionActive(pathname, child.link.pathname))
    )
    setOpenItems(current ? { [current.title]: true } : {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname])

  return (
    <Aside className={open ? 'open' : ''}>
      <AsideContent onClick={(event) => { if (event.target.closest('a')) onNavigate?.() }}>
        <AsideHeader>
          <div>
            <LogoIcon />
          </div>
        </AsideHeader>

        {companies && companies.length > 0 && (
          <CompanyWrapper className="company-info">
            <div className="company-dropdown">
              <Text fontSize="10px" fontWeight="500" color="#7E8B99" margin="0 0 6px 0" uppercase>
                {t('company.sidebarTitle')}
              </Text>
              <CompanySelectBox>
                <Select
                  outlined
                  fullWidth
                  value={selectedCompanyId}
                  options={companies}
                  renderOptions={company => (
                    <MenuItem key={company.id} value={company.id}>
                      {company.name}
                      {isManager ? ` (id: ${company.id})` : ''}
                    </MenuItem>
                  )}
                  onChange={handleCompanyChange}
                />
              </CompanySelectBox>
            </div>
          </CompanyWrapper>
        )}

        <BusinessType />

        {access.isAccessDisabled && (
          <Text fontSize="13px" color="#555F6D" margin="12px 16px">
            Доступ к этому парку отключён. Обратитесь к владельцу парка.
          </Text>
        )}

        <MainUl>
          {sidebarItems.map(({ icon, title, childrens, link, badgeDot, badgeTitle }) => {
            const hasChildren = childrens?.length > 0
            const firstChild = hasChildren ? childrens[0].link : null
            // Раздел с вложенным списком ведёт себя как обычный: щелчок
            // подсвечивает строку, раскрывает список и сразу открывает первый
            // пункт. Раньше по нему ничего не происходило, пока не попадёшь
            // в сам подпункт
            const handleClick = () => {
              if (!hasChildren) return
              toggleItem(title)
              if (!openItems[title] && firstChild) navigate({ ...firstChild })
            }
            const parentActive = hasChildren
              && childrens.some((child) => isSectionActive(pathname, child.link.pathname))

            return (
              <li key={title} className="link">
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  {link && !hasChildren ? (
                    <NavLink
                      to={{ ...link }}
                      className={() => (isSectionActive(pathname, link.pathname) ? 'active' : '')}
                      style={{ position: 'relative', display: 'flex', alignItems: 'center' }}
                    >
                      {icon}
                      {title}
                      {badgeDot && <BagdeSmall className="attention-dot" title={badgeTitle} />}
                      {badgeDot && <ItemAttention className="attention">Есть новое</ItemAttention>}
                    </NavLink>
                  ) : (
                    <ParentRow
                      type="button"
                      onClick={handleClick}
                      className={parentActive ? 'active' : ''}
                    >
                      {icon}
                      {title}
                      {badgeDot && <BagdeSmall className="attention-dot" title={badgeTitle} />}
                      {badgeDot && <ItemAttention className="attention">Есть новое</ItemAttention>}
                      <Chevron className={openItems[title] ? 'open' : ''}><ChevronDownIcon /></Chevron>
                    </ParentRow>
                  )}
                </div>

                {hasChildren && openItems[title] && (
                  <SubUl>
                    {childrens.map(({ link: childLink, title: childTitle }) => (
                      <li key={`${childLink.pathname}${childLink.search}`}>
                        <NavLink
                          className={({ isActive }) => {
                            if (!childLink.searchParam?.value) return isActive ? 'active' : ''
                            return isActive && searchParams.get(childLink.searchParam.name) === childLink.searchParam.value
                              ? 'active'
                              : ''
                          }}
                          to={{ ...childLink }}
                        >
                          {childTitle}
                          {childLink.badge && (
                            <Bagde background="#fff" className="badge">
                              {requestsCount?.[childLink.searchParam.value]}
                            </Bagde>
                          )}
                        </NavLink>
                      </li>
                    ))}
                  </SubUl>
                )}
              </li>
            )
          })}
        </MainUl>

        <OffersCard />

        <AsideUser>
          <SidebarBottomBlock currentUser={currentUser} />
        </AsideUser>
      </AsideContent>
    </Aside>
  )
}

export default Sidebar
